import { AuthenticationError } from "@auth0/auth0-spa-js";
import { createFileRoute, type ParsedLocation, redirect } from "@tanstack/react-router";

import {
  logCallbackError,
  logCallbackFinished,
  logCallbackStarted,
} from "@/lib/breadcrumbs";
import { migrateSession } from "@/lib/auth/migrate-session";
import type { RootRouteContext } from "@/routes/-root/-root-context";

const beforeLoad = async (
  context: RootRouteContext,
  location: ParsedLocation,
): Promise<void> => {
  logCallbackStarted({ location });

  let returnTo: string | null | undefined = "/";
  let retry = 0;
  try {
    const result = await context.auth0AuthenticationClient.handleRedirectCallback<{
      returnTo?: string;
      retry?: number;
    }>(location.href);
    returnTo = result.appState?.returnTo ?? "/";
    retry = result.appState?.retry ?? 1;

    const auth0Token = await context.auth0AuthenticationClient.getTokenSilently();

    const { sessionToken, sessionJwt } = await migrateSession(
      auth0Token,
      context.auth0AuthenticationClient,
    );
    // Hydrate the session into the Stytch SDK (not just persist the tokens), so the
    // isSessionActive() early-return in rootBeforeLoad sees it on the post-callback
    // navigation. Otherwise rootBeforeLoad reads an empty in-memory session and
    // migrates a second time, creating an orphaned Stytch session.
    await context.stytchAuthenticationClient.hydrateSessionWithTokens(
      sessionToken,
      sessionJwt,
    );
  } catch (error) {
    logCallbackError({ location, returnTo, error });

    if (error instanceof AuthenticationError && error.error === "access_denied") {
      redirect({
        to: "/",
        throw: true,
        search: {
          accessDenied: true,
          cause:
            error.error_description !== "access_denied" ? error.error_description : "",
        },
      });
      return;
    }

    if (error instanceof AuthenticationError && error.error === "missing_transaction") {
      // Invalid State: Tenta novamente
      redirect({
        to: "/auth/login",
        throw: true,
        search: {
          returnTo,
          retry: retry + 1,
        },
      });
      return;
    }
  }

  logCallbackFinished({ location, returnTo });

  redirect({
    to: returnTo,
    throw: true,
  });
};

export const Route = createFileRoute("/auth/callback")({
  async beforeLoad({ context, location }) {
    await beforeLoad(context, location);
  },
});
